import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff } from 'lucide-react';
import AuthLayout from '../components/Auth/AuthLayout';
import { Button } from '../components/UI/button';
import { Input } from '../components/UI/input';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';
import { colors } from '../config/colors';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { login, loading, error, isAuthenticated, clearError } = useAuth();
  const { t } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const onSubmit = async (data) => {
    const result = await login(data);
    if (result.success) {
      toast.success(t('authLoginSuccess'));
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } else {
      toast.error(result.error || t('authLoginFailed'));
    }
  };

  return (
    <AuthLayout>
      <div className="flex w-full flex-col justify-center gap-8">
        <div className="flex flex-col gap-3 shrink-0 w-full">
          <h1
            className="font-semibold text-[1.875rem] leading-9 tracking-tight shrink-0 w-full"
            style={{ color: colors.text.primary }}
          >
            {t('authLoginTitle')}
          </h1>
        </div>

        <form className="flex flex-col gap-6 shrink-0 w-full" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-6 w-full">
            <div className="flex flex-col gap-1.5 w-full">
              <label
                htmlFor="email"
                className="font-medium text-sm leading-5"
                style={{ color: colors.text.primary }}
              >
                {t('authEmailLabel')}
              </label>
              <Input
                {...register('email', {
                  required: true,
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email',
                  },
                })}
                id="email"
                type="email"
                autoComplete="email"
                placeholder={t('authEmailPlaceholder')}
              />
              {errors.email && (
                <p className="text-sm" style={{ color: colors.danger[500] }}>
                  {errors.email.message === 'Invalid email' ? t('authValidationInvalidEmail') : t('authValidationRequired')}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-3 w-full">
              <div className="flex flex-col gap-1.5 w-full">
                <label
                  htmlFor="password"
                  className="font-medium text-sm leading-5"
                  style={{ color: colors.text.primary }}
                >
                  {t('authPasswordLabel')}
                </label>
                <div className="relative">
                  <Input
                    {...register('password', { required: true })}
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder={t('authPasswordPlaceholder')}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t('authHidePassword') : t('authShowPassword')}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" style={{ color: colors.text.tertiary }} />
                    ) : (
                      <Eye className="h-5 w-5" style={{ color: colors.text.tertiary }} />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm" style={{ color: colors.danger[500] }}>
                    {t('authValidationRequired')}
                  </p>
                )}
              </div>
              <div className="flex items-center justify-end w-full">
                <Link
                  to="/forgot-password"
                  className="font-medium text-base hover:underline"
                  style={{ color: colors.text.accent }}
                >
                  {t('authForgotPassword')}
                </Link>
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full" variant="pill" size="pill">
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <LoadingSpinner size="sm" />
                  {t('authSigningIn')}
                </span>
              ) : (
                t('authLoginButton')
              )}
            </Button>
          </div>

          {error && (
            <div
              className="rounded-lg p-4 text-sm"
              style={{
                backgroundColor: colors.danger[50],
                color: colors.danger[700],
              }}
            >
              {error}
            </div>
          )}
        </form>
      </div>

      <div className="flex flex-col gap-1 items-center shrink-0 w-full mt-auto pt-10">
        <p
          className="font-normal text-base text-center w-full"
          style={{ color: colors.text.secondary }}
        >
          {t('authTermsPrefix')}{' '}
          <Link
            to="/settings"
            className="font-medium text-base hover:underline"
            style={{ color: colors.text.accent }}
          >
            {t('authTermsAndPrivacy')}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default Login;
